import { roleCanViewAudit, type PermissionCode, type SessionUser } from "@rbim/shared";
import {
  Activity,
  BarChart3,
  Database,
  DatabaseBackup,
  FilePlus2,
  FileText,
  Home,
  Info,
  LayoutDashboard,
  RefreshCw,
  ScanSearch,
  Settings,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";

export type NavigationItem = {
  label: string;
  path: string;
  permission: PermissionCode;
  anyPermissions?: readonly PermissionCode[];
  icon: typeof LayoutDashboard;
};

const operationalNavigation: NavigationItem[] = [
  { label: "Dashboard", path: "/", permission: "dashboard.view", icon: LayoutDashboard },
  { label: "Add New Data", path: "/add-new-data", permission: "record.create", icon: FilePlus2 },
  { label: "Household Profiles", path: "/households", permission: "record.view", icon: Home },
  { label: "Individual Profiles", path: "/individuals", permission: "record.view", icon: UserRound },
  { label: "Record Review", path: "/record-review", permission: "record.review", icon: ScanSearch },
  { label: "Certificates", path: "/certificates", permission: "certificate.view", icon: FileText },
  { label: "Reports", path: "/reports", permission: "report.view", icon: BarChart3 },
  { label: "AI Assistant", path: "/ai-assistant", permission: "ai.query", icon: Sparkles },
  { label: "Data Management", path: "/data-management", permission: "data.export", anyPermissions: ["data.import", "data.export"], icon: Database },
  { label: "Settings", path: "/settings", permission: "settings.manage", icon: Settings },
  { label: "About RBIM", path: "/about", permission: "dashboard.view", icon: Info },
];

const mayorViewerNavigation = operationalNavigation.filter(
  (item) => item.path === "/" || item.path === "/about",
);

const delegatedAdministrationNavigation: NavigationItem[] = [
  { label: "User Management", path: "/users", permission: "account.manage", icon: Users },
  { label: "Audit History", path: "/audit", permission: "audit.view", icon: Activity },
  { label: "Synchronization", path: "/synchronization", permission: "sync.monitor", icon: RefreshCw },
  { label: "Backup Status", path: "/backup-status", permission: "backup.monitor", icon: DatabaseBackup },
];

const platformNavigation: NavigationItem[] = [
  { label: "System Dashboard", path: "/", permission: "system.monitor", icon: LayoutDashboard },
  { label: "User Management", path: "/users", permission: "account.manage", icon: Users },
  { label: "Audit History", path: "/audit", permission: "audit.view", icon: Activity },
  { label: "Synchronization", path: "/synchronization", permission: "sync.monitor", icon: RefreshCw },
  { label: "Backup Status", path: "/backup-status", permission: "backup.monitor", icon: DatabaseBackup },
  { label: "Settings", path: "/settings", permission: "settings.manage", icon: Settings },
];

function candidateNavigation(user: SessionUser) {
  if (user.role === "TECHNICAL_ADMINISTRATOR") return platformNavigation;
  if (user.role === "SYSTEM_ADMINISTRATOR") {
    return uniqueByPath([...platformNavigation, ...operationalNavigation, ...delegatedAdministrationNavigation]);
  }
  if (user.role === "MAYOR_VIEWER") return mayorViewerNavigation;
  return [...operationalNavigation, ...delegatedAdministrationNavigation];
}

function uniqueByPath(items: NavigationItem[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.path)) return false;
    seen.add(item.path);
    return true;
  });
}

function canUseNavigationItem(user: SessionUser, item: NavigationItem) {
  if (
    item.path === "/ai-assistant" &&
    (user.role === "BARANGAY_ENCODER" || user.role === "MAYOR_VIEWER")
  ) return false;

  const allowedByPermission = item.anyPermissions
    ? item.anyPermissions.some((permission) => user.permissions.includes(permission))
    : user.permissions.includes(item.permission);
  if (!allowedByPermission) return false;
  if (item.path === "/audit") return roleCanViewAudit(user.role);
  return true;
}

export function navigationFor(user: SessionUser) {
  return candidateNavigation(user)
    .filter((item) => canUseNavigationItem(user, item))
    .map((item) => (
      user.role === "BARANGAY_SUPERVISOR" && item.path === "/ai-assistant"
        ? { ...item, label: "LIRA AI" }
        : item
    ));
}

export function canAccessPortalPath(user: SessionUser, path: string) {
  const items = candidateNavigation(user).filter((entry) => entry.path === path);
  return items.length > 0
    ? items.some((item) => canUseNavigationItem(user, item))
    : path === "/access-denied";
}
